import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import type { OnchainRecoveryContext } from "./onchainRecoveryCodec";

export const EVM_RECOVERY_REGISTRY_ABI = [
  "function registryVersion() view returns(uint256)",
  "function recordCount() view returns(uint256)",
  "function recordIndexPlusOne(bytes32) view returns(uint256)",
  "function publicationBlock(uint256) view returns(uint256)",
  "function publish((address pool,uint8 kind,bytes32 locator,bytes envelope) record) returns(uint256 index,bool inserted)",
  "function publishBatch((address pool,uint8 kind,bytes32 locator,bytes envelope)[] records) returns(uint256[] indices)",
  "function publishExistingNote(address pool,bytes32 commitment,bytes envelope) returns(uint256 index,bool inserted)",
  "function readPage(uint256 cursor,uint256 limit) view returns((address pool,uint8 kind,bytes32 locator,bytes envelope)[] page,uint256 nextCursor,uint256 total)",
  "event RecoveryPublished(uint256 indexed index,address indexed pool,bytes32 indexed locator,uint8 kind,bytes32 contentHash,bytes envelope)",
] as const;

const registryInterface = new ethers.Interface(EVM_RECOVERY_REGISTRY_ABI);
const MAX_BATCH = 16;
const MAX_PAGE = 32;

export interface EvmRecoveryRegistryConfig {
  address: string;
  chainId: number;
  runtimeCodehash: string;
}

/** Absence disables rollout; a partial/malformed configuration fails closed. */
export function configuredEvmRecoveryRegistry(
  chainId = EVM_CONFIG.CHAIN_ID,
): EvmRecoveryRegistryConfig | null {
  const address =
    chainId === 84532
      ? process.env.REACT_APP_BASE_RECOVERY_REGISTRY
      : chainId === 6281971
        ? process.env.REACT_APP_DOGEOS_CHIKYU_RECOVERY_REGISTRY
        : undefined;
  const runtimeCodehash =
    chainId === 84532
      ? process.env.REACT_APP_BASE_RECOVERY_REGISTRY_CODEHASH
      : chainId === 6281971
        ? process.env.REACT_APP_DOGEOS_CHIKYU_RECOVERY_REGISTRY_CODEHASH
        : undefined;
  if (!address && !runtimeCodehash) return null;
  if (
    !address ||
    !ethers.isAddress(address) ||
    address === ethers.ZeroAddress ||
    !runtimeCodehash ||
    !ethers.isHexString(runtimeCodehash, 32)
  )
    throw new EvmRecoveryPublicationError(
      "On-chain recovery deployment is incompletely configured.",
    );
  return {
    address: ethers.getAddress(address),
    chainId,
    runtimeCodehash: runtimeCodehash.toLowerCase(),
  };
}

export interface EvmRecoveryRecord {
  pool: string;
  kind: 1 | 2 | 3;
  locator: string;
  envelope: Uint8Array | string;
}

export interface EvmRecoveryReadOptions {
  cursor: bigint;
  limit?: number;
  blockTag?: ethers.BlockTag;
}

interface Cancellation {
  signal?: AbortSignal;
  isCurrent?: () => boolean;
}

export interface EvmRecoveryVerificationOptions extends Cancellation {
  /** Confirmed mode is explicitly weaker than finality; never silently substituted. */
  finality?: "finalized" | "confirmed";
  confirmations?: number;
}

export class EvmRecoveryPublicationError extends Error {
  constructor(
    message: string,
    readonly transactionHash?: string,
  ) {
    super(message);
    this.name = "EvmRecoveryPublicationError";
  }
}

function checkCurrent(options: Cancellation): void {
  if (options.signal?.aborted || options.isCurrent?.() === false)
    throw new EvmRecoveryPublicationError(
      "Recovery publication cancelled. Any submitted transaction is preserved.",
    );
}

function normalizeRecord(
  record: EvmRecoveryRecord,
): EvmRecoveryRecord & { envelope: string } {
  if (
    !record ||
    !ethers.isAddress(record.pool) ||
    ![1, 2, 3].includes(Number(record.kind)) ||
    !ethers.isHexString(record.locator, 32) ||
    record.locator === ethers.ZeroHash
  )
    throw new EvmRecoveryPublicationError("Invalid recovery record metadata.");
  const envelope = ethers.hexlify(record.envelope);
  const bytes = ethers.getBytes(envelope);
  if (bytes.length < 62 || bytes.length > 2048 || bytes[0] !== 1)
    throw new EvmRecoveryPublicationError("Invalid recovery envelope framing.");
  if (
    record.kind !== 3 &&
    ethers.getAddress(record.pool) === ethers.ZeroAddress
  )
    throw new EvmRecoveryPublicationError(
      "A note recovery record requires a pool.",
    );
  return {
    pool: ethers.getAddress(record.pool),
    kind: Number(record.kind) as 1 | 2 | 3,
    locator: record.locator.toLowerCase(),
    envelope: envelope.toLowerCase(),
  };
}

export function evmRecoveryRecordFromContext(
  context: OnchainRecoveryContext,
  envelope: Uint8Array | string,
  chainId: number,
): EvmRecoveryRecord {
  if (
    context.chain !== "evm" ||
    !ethers.isHexString(context.chainDomain, 32) ||
    BigInt(context.chainDomain) !== BigInt(chainId)
  )
    throw new EvmRecoveryPublicationError(
      "Recovery context is for another network.",
    );
  const pool = ethers.getBytes(context.pool);
  if (
    (pool.length !== 20 && pool.length !== 32) ||
    (pool.length === 32 && pool.slice(0, 12).some((x) => x !== 0))
  )
    throw new EvmRecoveryPublicationError("Invalid recovery pool context.");
  return normalizeRecord({
    pool: ethers.hexlify(pool.slice(-20)),
    kind: context.kind,
    locator: context.locator,
    envelope,
  });
}

export function hashEvmRecoveryRecord(record: EvmRecoveryRecord): string {
  const normalized = normalizeRecord(record);
  return ethers.keccak256(
    ethers.AbiCoder.defaultAbiCoder().encode(
      ["address", "uint8", "bytes32", "bytes"],
      [
        normalized.pool,
        normalized.kind,
        normalized.locator,
        normalized.envelope,
      ],
    ),
  );
}

async function verifyRegistry(
  provider: ethers.Provider,
  config: EvmRecoveryRegistryConfig,
): Promise<void> {
  if (
    !Number.isSafeInteger(config.chainId) ||
    config.chainId <= 0 ||
    !ethers.isAddress(config.address) ||
    ethers.getAddress(config.address) === ethers.ZeroAddress ||
    !ethers.isHexString(config.runtimeCodehash, 32)
  )
    throw new EvmRecoveryPublicationError(
      "Invalid recovery registry configuration.",
    );
  const [network, code] = await Promise.all([
    provider.getNetwork(),
    provider.getCode(config.address),
  ]);
  if (
    network.chainId !== BigInt(config.chainId) ||
    code === "0x" ||
    ethers.keccak256(code) !== config.runtimeCodehash.toLowerCase()
  )
    throw new EvmRecoveryPublicationError(
      "Recovery registry network or deployment does not match.",
    );
}

async function call(
  provider: ethers.Provider,
  config: EvmRecoveryRegistryConfig,
  name: string,
  args: readonly unknown[],
  blockTag: ethers.BlockTag = "latest",
): Promise<ethers.Result> {
  return registryInterface.decodeFunctionResult(
    name,
    await provider.call({
      to: config.address,
      data: registryInterface.encodeFunctionData(name, args),
      blockTag,
    }),
  );
}

async function readPageUnchecked(
  provider: ethers.Provider,
  config: EvmRecoveryRegistryConfig,
  options: EvmRecoveryReadOptions,
) {
  const limit = options.limit ?? MAX_PAGE;
  if (
    options.cursor < 0n ||
    !Number.isSafeInteger(limit) ||
    limit < 1 ||
    limit > MAX_PAGE
  )
    throw new EvmRecoveryPublicationError("Invalid recovery page request.");
  const result = await call(
    provider,
    config,
    "readPage",
    [options.cursor, limit],
    options.blockTag,
  );
  const records = Array.from(result[0] as ethers.Result).map((record: any) =>
    normalizeRecord({
      pool: record.pool,
      kind: Number(record.kind) as 1 | 2 | 3,
      locator: record.locator,
      envelope: record.envelope,
    }),
  );
  const nextCursor = BigInt(result[1]),
    total = BigInt(result[2]);
  if (
    records.length > limit ||
    nextCursor !== options.cursor + BigInt(records.length) ||
    nextCursor > total ||
    options.cursor > total ||
    (records.length === 0 && nextCursor < total)
  )
    throw new EvmRecoveryPublicationError(
      "Invalid recovery registry page response.",
    );
  return { records, nextCursor, total };
}

/** Current contract state is sufficient; no recovery endpoint or historical logs are used. */
export async function readEvmRecoveryPage(
  provider: ethers.Provider,
  config: EvmRecoveryRegistryConfig,
  options: EvmRecoveryReadOptions,
) {
  await verifyRegistry(provider, config);
  return readPageUnchecked(provider, config, options);
}

function uniqueRecords(records: readonly EvmRecoveryRecord[]) {
  if (records.length === 0 || records.length > MAX_BATCH)
    throw new EvmRecoveryPublicationError(
      "Recovery publication requires 1 to 16 records.",
    );
  return [
    ...new Map(
      records.map((record) => {
        const normalized = normalizeRecord(record);
        return [hashEvmRecoveryRecord(normalized), normalized] as const;
      }),
    ).values(),
  ];
}

async function locateRecord(
  provider: ethers.Provider,
  config: EvmRecoveryRegistryConfig,
  record: EvmRecoveryRecord,
  blockTag: ethers.BlockTag = "latest",
) {
  const hash = hashEvmRecoveryRecord(record);
  const indexPlusOne = BigInt(
    (await call(provider, config, "recordIndexPlusOne", [hash], blockTag))[0],
  );
  if (indexPlusOne === 0n) return null;
  const index = indexPlusOne - 1n;
  const [page, block] = await Promise.all([
    readPageUnchecked(provider, config, { cursor: index, limit: 1, blockTag }),
    call(provider, config, "publicationBlock", [index], blockTag),
  ]);
  if (
    page.records.length !== 1 ||
    hashEvmRecoveryRecord(page.records[0]) !== hash
  )
    throw new EvmRecoveryPublicationError(
      "Stored recovery bytes do not match the requested record.",
    );
  return { index, publicationBlock: BigInt(block[0]) };
}

/** Returns a transaction a connected wallet or sponsor can submit unchanged. */
export async function prepareEvmRecoveryPublication(
  provider: ethers.Provider,
  config: EvmRecoveryRegistryConfig,
  records: readonly EvmRecoveryRecord[],
  options: Cancellation = {},
) {
  checkCurrent(options);
  const normalized = uniqueRecords(records);
  await verifyRegistry(provider, config);
  const found = await Promise.all(
    normalized.map((record) => locateRecord(provider, config, record)),
  );
  checkCurrent(options);
  const missingRecords = normalized.filter((_, index) => found[index] === null);
  return {
    records: normalized,
    missingRecords,
    transaction: missingRecords.length
      ? {
          to: ethers.getAddress(config.address),
          value: 0n,
          data: registryInterface.encodeFunctionData("publishBatch", [
            missingRecords,
          ]),
          chainId: BigInt(config.chainId),
        }
      : null,
  };
}

/** Checks exact immutable bytes from recent state and their creation-block finality. */
export async function verifyEvmRecoveryPublication(
  provider: ethers.Provider,
  config: EvmRecoveryRegistryConfig,
  records: readonly EvmRecoveryRecord[],
  options: EvmRecoveryVerificationOptions = {},
) {
  checkCurrent(options);
  const normalized = uniqueRecords(records);
  const mode = options.finality ?? "finalized";
  const confirmations = options.confirmations ?? 1;
  if (
    !Number.isSafeInteger(confirmations) ||
    confirmations < 1 ||
    !["finalized", "confirmed"].includes(mode)
  )
    throw new EvmRecoveryPublicationError(
      "Invalid recovery confirmation policy.",
    );
  await verifyRegistry(provider, config);
  const [snapshot, head] = await Promise.all([
    provider.getBlock("latest"),
    provider.getBlock(mode === "finalized" ? "finalized" : "latest"),
  ]);
  if (!snapshot?.hash || !head?.hash)
    throw new EvmRecoveryPublicationError(
      "Recovery publication confirmation is unavailable.",
    );
  const found = await Promise.all(
    normalized.map((record) =>
      locateRecord(provider, config, record, snapshot.number),
    ),
  );
  const sameBlock = await provider.getBlock(snapshot.number);
  if (sameBlock?.hash !== snapshot.hash)
    throw new EvmRecoveryPublicationError(
      "Network changed while verifying recovery publication. Retry safely.",
    );
  checkCurrent(options);
  const published = found.every((record) => record !== null);
  const verified =
    published &&
    found.every(
      (record) =>
        record !== null &&
        record.publicationBlock <=
          BigInt(head.number) -
            BigInt(mode === "confirmed" ? confirmations - 1 : 0),
    );
  return {
    published,
    verified,
    finality: mode,
    indices: found.map((record) => record?.index ?? null),
    publicationBlocks: found.map((record) => record?.publicationBlock ?? null),
    checkedBlockNumber: snapshot.number,
    checkedBlockHash: snapshot.hash,
  };
}

export interface PublishEvmRecoveryOptions extends EvmRecoveryVerificationOptions {
  timeoutMs?: number;
  pollIntervalMs?: number;
  onSubmitted?: (transactionHash: string) => void | Promise<void>;
}

/** Timeout/cancellation never removes a publication; retries deduplicate exact records. */
export async function publishEvmRecoveryRecords(
  signer: ethers.Signer,
  config: EvmRecoveryRegistryConfig,
  records: readonly EvmRecoveryRecord[],
  options: PublishEvmRecoveryOptions = {},
) {
  const provider = signer.provider;
  if (!provider)
    throw new EvmRecoveryPublicationError(
      "Recovery publication needs a connected wallet provider.",
    );
  const expectedAccount = await signer.getAddress();
  const prepared = await prepareEvmRecoveryPublication(
    provider,
    config,
    records,
    options,
  );
  const timeoutMs = options.timeoutMs ?? 180_000,
    pollIntervalMs = options.pollIntervalMs ?? 2_000;
  if (
    !Number.isSafeInteger(timeoutMs) ||
    timeoutMs < 1 ||
    !Number.isSafeInteger(pollIntervalMs) ||
    pollIntervalMs < 1
  )
    throw new EvmRecoveryPublicationError(
      "Invalid recovery publication timeout.",
    );
  let transactionHash: string | undefined;
  checkCurrent(options);
  if (prepared.transaction) {
    if ((await signer.getAddress()) !== expectedAccount)
      throw new EvmRecoveryPublicationError(
        "Wallet changed before recovery publication.",
      );
    checkCurrent(options);
    const response = await signer.sendTransaction(prepared.transaction);
    transactionHash = response.hash;
    await options.onSubmitted?.(transactionHash);
  }
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const result = await verifyEvmRecoveryPublication(
      provider,
      config,
      prepared.records,
      options,
    );
    if (result.verified) return { ...result, transactionHash };
    if (Date.now() >= deadline)
      throw new EvmRecoveryPublicationError(
        "Recovery publication is still awaiting confirmation. Retry safely; published data is preserved.",
        transactionHash,
      );
    await new Promise<void>((resolve) =>
      setTimeout(resolve, Math.min(pollIntervalMs, deadline - Date.now())),
    );
  }
}
